import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ═══════════════════════════════════════════════════════════════════
// Field context — links label, hint and error to the control
// ═══════════════════════════════════════════════════════════════════

interface FieldContextValue {
  id: string;
  invalid: boolean;
  describedBy?: string;
}

const FieldContext = React.createContext<FieldContextValue | null>(null);

/**
 * Resolves the accessibility wiring of a control inside a `FormField`:
 * the shared `id`, `aria-invalid` and `aria-describedby`. Explicit props
 * on the control win over the field's values.
 */
function useFieldControl(
  id: string | undefined,
  error: boolean | undefined,
  describedBy: string | undefined
) {
  const field = React.useContext(FieldContext);
  const invalid = error ?? field?.invalid ?? false;
  const described = [describedBy, field?.describedBy].filter(Boolean).join(' ');
  return {
    id: id ?? field?.id,
    'aria-invalid': invalid || undefined,
    'aria-describedby': described || undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════
// FormField
// ═══════════════════════════════════════════════════════════════════

export interface FormFieldProps {
  label: React.ReactNode;
  /** Short helper text under the control. */
  hint?: React.ReactNode;
  required?: boolean;
  /** Error message; renders with `role="alert"` and marks the control invalid. */
  error?: string;
  /** Id of the control. Generated when omitted, so a kit control inside links automatically. */
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Label, control, hint and error as one block. Kit controls (`Input`,
 * `Textarea`, `Select`) pick up the id and the aria wiring from context;
 * foreign controls pass `htmlFor` and set the same `id` themselves.
 */
export function FormField({
  label,
  hint,
  required,
  error,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  const generatedId = React.useId();
  const id = htmlFor ?? generatedId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const context = React.useMemo<FieldContextValue>(
    () => ({
      id,
      invalid: Boolean(error),
      describedBy: describedBy || undefined,
    }),
    [id, error, describedBy]
  );

  return (
    <FieldContext.Provider value={context}>
      <div className={cn('space-y-1.5', className)}>
        <label htmlFor={id} className="block text-sm font-medium text-ink">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {children}
        {hint && !error && (
          <p id={hintId} className="text-xs text-mute">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Controls
// ═══════════════════════════════════════════════════════════════════

/**
 * Shared control face: 42 px, 10 px radius, paper surface with a hairline,
 * primary ring on focus, danger ring when invalid. No shadow.
 */
const controlClassName =
  'w-full rounded-control bg-paper text-[15px] text-ink ring-1 ring-inset ring-line placeholder:text-mute transition-[box-shadow] duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 aria-[invalid=true]:ring-danger';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Leading icon, e.g. from lucide-react. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Marks the input invalid; inside a `FormField` the field's `error` does this. */
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon: Icon,
      error,
      id,
      className,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const a11y = useFieldControl(id, error, describedBy);
    const input = (
      <input
        ref={ref}
        {...a11y}
        className={cn(
          controlClassName,
          'h-[42px] px-3.5',
          Icon && 'pl-10',
          className
        )}
        {...props}
      />
    );
    if (!Icon) return input;
    return (
      <div className="relative">
        <Icon
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-mute"
          aria-hidden="true"
        />
        {input}
      </div>
    );
  }
);
Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { error, id, className, 'aria-describedby': describedBy, ...props },
    ref
  ) => {
    const a11y = useFieldControl(id, error, describedBy);
    return (
      <textarea
        ref={ref}
        {...a11y}
        className={cn(
          controlClassName,
          'min-h-[100px] resize-y px-3.5 py-2.5',
          className
        )}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
}

/** Native select with the kit's control face and its own chevron. */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      error,
      id,
      className,
      children,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const a11y = useFieldControl(id, error, describedBy);
    return (
      <div className="relative">
        <select
          ref={ref}
          {...a11y}
          className={cn(
            controlClassName,
            'h-[42px] appearance-none pl-3.5 pr-10',
            className
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-mute"
          aria-hidden="true"
        />
      </div>
    );
  }
);
Select.displayName = 'Select';

export { Input, Textarea, Select };
